import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { connect } from 'react-redux';

// Actions
import {
  ADD_HOLDINGS_TO_PORTFOLIO_FROM_CSV,
  calculatePortfolio,
  changeHoldingDollars,
  changeHoldingCostBasis,
  changeHoldingLotDollars,
  changeHoldingPurchaseDate,
  changeHoldingLotUnits,
  changeHoldingSymbol,
  changeHoldingUnits,
  changeHoldingWeight,
  clearProductAdded,
  recalculatePortfolioAllocation,
  removeHoldingFromPortfolio,
  setHoldingToCompareComplianceAlts,
  setHoldingToReplace,
} from '@/redux/modules/edit-portfolio';
import { resetProcessing } from '@/redux/modules/ai-statement-reader';
import { selectShelfStatusedPortfolioHoldings } from '@/redux/modules/shelf-management/selectors';
import { setFocusedQuickSearchEditPorfolio } from '@/redux/modules/quick-search';
import { getCustomProxies } from '@/redux/modules/custom-proxies';

// Components
import CompareAlternatives from '@/views/components/modals/CompareAlternatives';
import ComparisonItem from '@/types/comparison-item';
import ConnectedPrivateComponent from '@/views/components/PrivateComponent';
import DataTableV2 from '@/views/components/data/DataTableV2/DataTableV2';
import EmptySearchResults from '@/views/components/data/DataTableV2/EmptySearchResults';
import EmptyStateCard from '@/views/components/misc/EmptyStateCard';
import PortfolioUploadModal from '@/views/components/modals/portfolio-upload/PortfolioUploadModal';
import ShelfStatus from '@/views/components/shelf-management/ShelfStatus';
import { SelectInput, SelectOption, DatePicker } from '@/views/components/inputs';
import UnitInput, { UnitSymbolSection } from '@/views/components/inputs/UnitInput';
import { IconButton, PrimaryLink } from '@/views/components/buttons';
import { Tooltip } from '@/views/components/tooltips';
import { withErrorBoundary } from '@/views/components/errors';
import StatusFeedbackModal from '@/views/components/modals/StatusFeedbackModal';
import PortfolioAiStatementReaderModal from '@/views/components/modals/portfolio-ai-statement-reader/PortfolioAIStatementReaderModal';
import MultiPortfolioAiStatementReaderModal from '@/views/components/modals/multi-portfolio-ai-statement-reader/MultiPortfolioAiStatementReaderModal';
import { FormattedMessageV2 } from '@/views/components/i18n';
import PortfolioEditorHoldingsTableReplaceAction from './PortfolioEditorHoldingsTableAction/PortfolioEditorHoldingsTableReplaceAction';
import RateOfReturnModal from '@/views/components/modals/RateOfReturnModal';

// Enums
import { HoldingAllocationMode, PORTFOLIO_HOLDINGS_KEYS, PORTFOLIO_HOLDINGS_OTHER_TABLE_COLUMNS } from '@/enums/Portfolio';
import { HOLDING_TYPES, isHoldingMissingCostBasis, isInputDisabledForCostBasis } from '@/utils/portfolios';
import { ShelfStatus as ShelfStatusEnum } from '@/utils/shelf-management';
import { MISSING_PRODUCT_STEP } from '@/enums/MissingProduct';

// Libraries
import classNames from 'classnames';
import { ColumnDef, Row, useReactTable, VisibilityState } from '@tanstack/react-table';
import { debounce, filter, includes } from 'lodash';

// Types
import AppRegion from '@/types/app-region';
import Fund from '@/types/fund';
import Holding from '@/types/holding';
import Locale from '@/types/locale';
import LocalizedString from '@/types/localized-string';
import Nullable from '@/types/nullable';
import Portfolio from '@/types/portfolio';
import PortfolioHolding from '@/types/portfolio-holding';
import Stock from '@/types/stock';
import User from '@/types/user';
import { ReduxState } from '@/redux/modules/types';
import { TableClassNames } from '@/types/data-table';
import { Status } from '@/types/status';
import { CustomProxiesData } from '@/types/custom-proxies-state';
import CUSTOM_PROXY_ITEM from '@/types/custom-proxy-item';
import { Lot } from '@capintel/instruments-lib';

// Utils
import helpers from '@/utils/disclaimers/helpers';
import notification from '@/utils/notification';
import useScreenSize from '@/hooks/useScreenSize';
import { canAccessAIStatementReader, canAccessMultiPortfoliosStatementReader, canAccessTaxAnalysis, canAccessUploadCSV } from '@/utils/feature-access';
import { getShelfNameFromShelvableInstrument } from '@/utils/user-shelf';
import { getLatestPriceForHolding } from '@/utils/calculation-utils';
import { customSorting } from '@/utils/data-table';
import { formatCurrency, formatLocalizedString, formatMessage, formatMessageWithLocale, formatNumber } from '@/utils/i18n';
import { showError } from '@/utils/errors';
import { getPortfolioMissingOrInvalidHoldings } from '@/utils/portfolio/utils';
import { PERMISSIONS, userHasPermission, userHasPermissionEnabled } from '@/utils/permissions';
import { isHoldingACustomProxy, findCustomProxyByKey } from '@/utils/custom-proxies';
import { clearCustomYieldCache } from '@/utils/portfolio/customYieldCache';
import { validateUserAppRegion } from '@/utils/users';

// Assets
import EmptyPorfolioAIStatementReader from '@/images/design-system-v2/empty-state/emptyPortfolioAIStatementReader.png';
import ProxyLogEntry from '@/types/portfolio-log-entry';
import Banner, { BannerVariant } from '@/views/components/common/Banner';

type DispatchProps = {
  changeHoldingDollars: (id: string, dollars: number, holding: Holding) => void;
  changeHoldingCostBasis: (costBasis: number, lotId: string, holdingId?: string) => void;
  changeHoldingLotDollars: (dollars: number, lotId: string, holdingId?: string) => void;
  changeHoldingPurchaseDate: (purchaseDate: Date | null, holdingId: string, lotId?: string) => void;
  changeHoldingLotUnits: (units: number, lotId: string, holdingId?: string) => void;
  changeHoldingSymbol: (id: string, symbol: string) => void;
  changeHoldingUnits: (id: string, units: number, holding: Holding) => void;
  changeHoldingWeight: (id: string, weight: number, holding: Holding) => void;
  clearProductAdded: () => void;
  getCustomProxies: () => Promise<CustomProxiesData>;
  recalculatePortfolioAllocation: () => void;
  removeHoldingFromPortfolio: (holding: PortfolioHolding) => void;
  resetProcessing: () => void;
  setFocusedQuickSearchEditPorfolio: (focused?: boolean, enableOnlyProxies?: boolean) => void;
  setHoldingToCompareComplianceAlts: (holdingToCompare?: PortfolioHolding) => void;
  setHoldingToReplace: (holdingToReplace?: PortfolioHolding) => void;
  calculatePortfolio: (comparisonItem?: ComparisonItem, reallocate?: boolean) => Promise<void>;
};

type StateProps = {
  isAddingHoldingsFromCSV: boolean;
  complianceAlternatives: Fund[] | Stock[];
  customProxies: CustomProxiesData;
  firstMissingHolding?: PortfolioHolding;
  hasRestrictedFlow: boolean;
  holdingToReplace?: PortfolioHolding;
  isFocusedQuickSearch?: boolean;
  isPortfolioValueLocked?: boolean;
  isReadOnly?: boolean;
  holdingToCompareComplianceAlts?: PortfolioHolding;
  locale: Locale;
  missingHoldingsIds: Array<string>;
  portfolio: Portfolio;
  productAdded?: PortfolioHolding;
  user?: Nullable<User>;
  comparisonItem?: ComparisonItem;
  showTaxDetails?: boolean;
};

type Props = DispatchProps &
  StateProps & {
    allocationMode: HoldingAllocationMode;
    portfolioHoldings: PortfolioHolding[];
    portfolioSearchQuery: string;
    onProxyLogChange?: (log: ProxyLogEntry[]) => void;
    showMissingCostBasisBanner?: boolean;
    tableRef: React.MutableRefObject<ReturnType<typeof useReactTable<PortfolioHolding>> | null>;
    assetTypeCache: Map<string, LocalizedString>;
    getHoldingData: (holding: PortfolioHolding) => Promise<Holding>;
    displayClearHoldingsModal: () => void;
  };

const PortfolioEditorHoldingsTable = (props: Props) => {
  // Variables Declaration
  const {
    allocationMode,
    comparisonItem,
    firstMissingHolding,
    hasRestrictedFlow,
    holdingToReplace,
    isFocusedQuickSearch,
    complianceAlternatives,
    customProxies,
    isReadOnly,
    locale,
    missingHoldingsIds,
    portfolio,
    portfolioHoldings,
    portfolioSearchQuery,
    productAdded,
    user,
    changeHoldingDollars,
    changeHoldingCostBasis,
    changeHoldingLotDollars,
    changeHoldingPurchaseDate,
    changeHoldingLotUnits,
    changeHoldingSymbol,
    changeHoldingUnits,
    changeHoldingWeight,
    clearProductAdded,
    getCustomProxies,
    holdingToCompareComplianceAlts,
    removeHoldingFromPortfolio,
    resetProcessing,
    setFocusedQuickSearchEditPorfolio,
    setHoldingToCompareComplianceAlts,
    setHoldingToReplace,
    isAddingHoldingsFromCSV,
    recalculatePortfolioAllocation,
    calculatePortfolio,
    onProxyLogChange,
    isPortfolioValueLocked,
    showTaxDetails,
    showMissingCostBasisBanner,
    tableRef,
    assetTypeCache,
    getHoldingData,
  } = props;

  const hideReplacedMessageDelay = 2000; // This seconds delay was defined in design
  const holdingsTableRef = React.useRef<HTMLDivElement>(null);
  const isAllocationModeWeight = allocationMode === HoldingAllocationMode.Weight;
  const isCompareAlternativeColumnVisible = userHasPermissionEnabled(user, PERMISSIONS.KYP, PERMISSIONS.KYP_STOCKS);
  const isShelfStatusColumnVisible = portfolioHoldings.some(h => h.flags?.length);
  const i18nBasePath = 'PortfolioEditorBuilder.Content.HoldingsTable';
  const isTaxAnalysisFeatureEnabled = canAccessTaxAnalysis(user, comparisonItem?.portfolioType);
  const canAccessLots = isTaxAnalysisFeatureEnabled && validateUserAppRegion(user as User, AppRegion.US);

  const tableClassNames: TableClassNames = {
    isBodyHoverActive: true,
  };

  const columnVisibilityStateObj = useMemo(
    () => ({
      [PORTFOLIO_HOLDINGS_KEYS.ADVISORY_FEE]: false,
      [PORTFOLIO_HOLDINGS_KEYS.FLAGS]: isShelfStatusColumnVisible,
      [PORTFOLIO_HOLDINGS_KEYS.UNITS]: !isAllocationModeWeight,
      [PORTFOLIO_HOLDINGS_KEYS.WEIGHT]: isAllocationModeWeight,
      [PORTFOLIO_HOLDINGS_OTHER_TABLE_COLUMNS.ACTION_COMPARE_ALTERNATIVES]: isCompareAlternativeColumnVisible,
    }),
    [isAllocationModeWeight, isCompareAlternativeColumnVisible, isShelfStatusColumnVisible],
  );

  const portfolioHoldingsData = useMemo(() => {
    const missingItems = portfolioHoldings.filter(holding => helpers.holdingIsMissingOrInvalid(holding));
    const correctItems = portfolioHoldings.filter(holding => !helpers.holdingIsMissingOrInvalid(holding));

    return [...missingItems, ...correctItems];
  }, [portfolioHoldings]);

  const portfolioHoldingsCount = useMemo(() => portfolioHoldingsData.length, [portfolioHoldingsData]);
  const missingHoldingsCount = useMemo(() => missingHoldingsIds.length, [missingHoldingsIds]);

  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(columnVisibilityStateObj);
  const [hasMissingHoldings, setHasMissingHoldings] = useState<boolean>(!!missingHoldingsIds.length);
  const [missingProductStep, setMissingProductStep] = useState<MISSING_PRODUCT_STEP>(MISSING_PRODUCT_STEP.INITIAL);
  const [replacedMissingHoldings, setReplacedMissingHoldings] = useState<boolean>(false);
  const [showCompareAlternativesModal, setShowCompareAlternativesModal] = useState<boolean>(holdingToCompareComplianceAlts !== undefined);
  const [showPortfolioUploadModal, setShowPortfolioUploadModal] = useState<boolean>(false);
  const [showPortfolioAIStatementReaderModal, setPortfolioAIStatementReaderModal] = useState<boolean>(false);
  const [replacementProxyItem, setReplacementProxyItem] = useState<CUSTOM_PROXY_ITEM | undefined>();
  const [missingHoldingsProxyLog, setMissingHoldingsProxyLog] = useState<ProxyLogEntry[]>([]);
  const { isMobile, isTablet } = useScreenSize();

  useEffect(() => {
    if (onProxyLogChange) {
      onProxyLogChange(missingHoldingsProxyLog);
    }
  }, [missingHoldingsProxyLog, onProxyLogChange]);

  // Reset expanded rows when showTaxDetails becomes false
  useEffect(() => {
    if (!showTaxDetails && tableRef.current) {
      tableRef.current.resetExpanded();
    }
  }, [showTaxDetails, tableRef]);

  const logProxyReplacement = useCallback((proxyLog: ProxyLogEntry) => {
    setMissingHoldingsProxyLog(prev => [...prev, proxyLog]);
  }, []);

  const memoizedRecalculatePortfolioAllocation = useCallback(() => {
    recalculatePortfolioAllocation();
  }, [recalculatePortfolioAllocation]);

  useEffect(() => {
    if (hasRestrictedFlow) {
      getCustomProxies();
    }
  }, [hasRestrictedFlow, getCustomProxies]);

  useEffect(() => {
    const stillContainsHoldings = !!portfolioHoldingsCount;
    const stillHaveMissingHoldings = !!missingHoldingsCount;
    const allMissingProductsAreResolved = hasMissingHoldings && !stillHaveMissingHoldings && stillContainsHoldings;

    if (stillHaveMissingHoldings) {
      const missingProductStep = isFocusedQuickSearch ? MISSING_PRODUCT_STEP.SEARCH : MISSING_PRODUCT_STEP.INITIAL;
      setMissingProductStep(prevStep => (prevStep === MISSING_PRODUCT_STEP.OPEN_MODAL ? prevStep : missingProductStep));
    }

    if (allMissingProductsAreResolved) {
      memoizedRecalculatePortfolioAllocation();
      setReplacedMissingHoldings(true);
    }

    setHasMissingHoldings(stillHaveMissingHoldings);
  }, [hasMissingHoldings, isFocusedQuickSearch, missingHoldingsCount, portfolioHoldingsCount, memoizedRecalculatePortfolioAllocation]);

  const prevValues = useRef({
    holdings: portfolio.holdings,
    discretionaryMer: portfolio.discretionaryMer,
    size: portfolio.size,
  });

  const totalAllocation = useMemo(() => portfolio.holdings.reduce((sum, holding) => sum + (holding.weight || 0), 0), [portfolio.holdings]);

  const hasAllocationsChanged = (prevHoldings: PortfolioHolding[], currentHoldings: PortfolioHolding[]): boolean => {
    if (prevHoldings.length !== currentHoldings.length) {
      return true;
    }

    return prevHoldings.some((prevHolding, index) => {
      const currentHolding = currentHoldings[index];
      return prevHolding.weight !== currentHolding.weight || prevHolding.units !== currentHolding.units || prevHolding.dollars !== currentHolding.dollars;
    });
  };

  useEffect(() => {
    const prev = prevValues.current;

    const allocationHasChanged = hasAllocationsChanged(prev.holdings, portfolio.holdings);
    const merHasChanged = prev.discretionaryMer !== portfolio.discretionaryMer;
    const sizeHasChanged = prev.size !== portfolio.size;

    const calculatePortfolioDebounce = debounce(() => {
      calculatePortfolio(comparisonItem, true).catch(showError);
    }, 500);

    if ((allocationHasChanged || merHasChanged || sizeHasChanged) && !hasMissingHoldings && !isPortfolioValueLocked) {
      calculatePortfolioDebounce();
    }

    // Update previous values for next render
    prevValues.current = {
      holdings: portfolio.holdings,
      discretionaryMer: portfolio.discretionaryMer,
      size: portfolio.size,
    };
    return () => {
      calculatePortfolioDebounce.cancel();
    };
  }, [
    totalAllocation,
    portfolio.holdings,
    portfolio.discretionaryMer,
    portfolio.size,
    hasMissingHoldings,
    calculatePortfolio,
    comparisonItem,
    isPortfolioValueLocked,
  ]);

  useEffect(() => {
    // Hide the notification message of the end of missing product flow after 2 sec
    if (replacedMissingHoldings) {
      const timeoutId = setTimeout(() => setReplacedMissingHoldings(false), hideReplacedMessageDelay);
      return () => clearTimeout(timeoutId);
    }
  }, [replacedMissingHoldings]);

  // Set the Column Visibility state after initial render
  useEffect(() => {
    setColumnVisibility(columnVisibilityStateObj);
  }, [columnVisibilityStateObj]);

  useEffect(() => {
    if (hasMissingHoldings && productAdded) {
      if (holdingToReplace && hasRestrictedFlow) {
        const proxyUsedWithoutKey = !findCustomProxyByKey(customProxies, holdingToReplace.proxyKey!);
        const proxyLog = {
          holding: {
            id: holdingToReplace._id,
            name: holdingToReplace.name,
          },
          replacedBy: {
            id: productAdded._id,
            name: productAdded.name,
          },
          proxyUsedWithoutKey,
          proxyUsed: true,
        };
        logProxyReplacement(proxyLog);
      }
      notification.success(
        formatMessage(`${i18nBasePath}.MissingHoldings.ProductAdded`, {
          product: formatLocalizedString(productAdded?.name),
        }),
      );
    }
    return () => clearProductAdded();
  }, [hasMissingHoldings, productAdded, clearProductAdded, holdingToReplace, hasRestrictedFlow]);

  // Clear cache when portfolio holdings change significantly
  useEffect(() => {
    // Clear cache on portfolio change to prevent stale data
    clearCustomYieldCache();
  }, [portfolio._id, portfolioHoldings.length]);

  // DISPATCH FUNCTIONS
  // Use the assetTypeCache and getHoldingData passed from parent to avoid duplication

  const missingCostBasisPages = useMemo(() => {
    if (!showTaxDetails || !portfolioHoldings.length || !tableRef.current || !isHoldingMissingCostBasis) return [];

    const { pageSize } = tableRef.current.getState().pagination;
    const pagesWithMissingCostBasis = new Set<number>();

    // Check each holding with its assetType to see if cost basis is missing
    portfolioHoldings.forEach((holding, index) => {
      const holdingId = holding.original.id;
      if (!holdingId) return;

      const assetType = assetTypeCache.get(holdingId);
      if (isHoldingMissingCostBasis(holding, assetType)) {
        const pageIndex = Math.floor(index / pageSize);
        pagesWithMissingCostBasis.add(pageIndex);
      }
    });

    return Array.from(pagesWithMissingCostBasis);
    // Only recalculate when assetTypeCache updates (which happens when holdings change)
    // portfolioHoldings reference changes too frequently from Redux
  }, [showTaxDetails, assetTypeCache]);

  const onAdvisoryFeeChange = async (portfolioHolding: PortfolioHolding, advisoryFee: number) => {
    // TODO
  };

  const onDollarsChange = useCallback(
    async (portfolioHolding: PortfolioHolding, dollars: number) => {
      const data = await getHoldingData(portfolioHolding);
      changeHoldingDollars(portfolioHolding._id, dollars, data);
    },
    [getHoldingData, changeHoldingDollars],
  );

  const onLotDollarsChange = (dollars: number, lotId: string, holdingId?: string) => {
    changeHoldingLotDollars(dollars, lotId, holdingId);
  };

  const onLotUnitsChange = (units: number, lotId: string, holdingId?: string) => {
    changeHoldingLotUnits(units, lotId, holdingId);
  };

  const onSymbolChange = (portfolioHolding: PortfolioHolding, symbol: string) => {
    changeHoldingSymbol(portfolioHolding._id, symbol);
  };

  const onUnitsChange = (portfolioHolding: PortfolioHolding, units: number, data: Holding) => {
    changeHoldingUnits(portfolioHolding._id, units, data);
  };

  const onCostBasisChange = (costBasis: number, holdingId: string, lotId?: string) => {
    changeHoldingCostBasis(costBasis, holdingId, lotId);
  };

  const onPurchaseDateChange = (purchaseDate: Date | null, holdingId: string, lotId?: string) => {
    changeHoldingPurchaseDate(purchaseDate, holdingId, lotId);
  };

  const onWeightChange = async (portfolioHolding: PortfolioHolding, weight: number) => {
    const data = await getHoldingData(portfolioHolding);
    changeHoldingWeight(portfolioHolding._id, weight, data);
  };

  const onCompareAlternativesForComplianceClose = () => {
    setShowCompareAlternativesModal(false);
    setHoldingToCompareComplianceAlts(undefined);
  };

  const compareActiveHoldingAlternatives = (portfolioHolding: PortfolioHolding) => {
    setHoldingToCompareComplianceAlts(portfolioHolding);
    setShowCompareAlternativesModal(true);
  };

  // CLASSES

  const computeAdditionalRowClassNames = ({ id, original, depth, getParentRow }: Row<PortfolioHolding>) => {
    // Check if this is a lot subrow (depth > 0) with missing cost basis or units
    const isLotRow = depth > 0;
    const holdingRow = !isLotRow ? original : getParentRow()?.original;
    const assetType = !isLotRow && holdingRow?.original?.id && assetTypeCache ? assetTypeCache.get(holdingRow.original.id) : undefined;

    // Helper to check if a lot has validation errors
    const hasLotValidationError = (lot: Lot) => !lot.costBasis || lot.costBasis === 0 || !lot.units || lot.units === 0;

    let rowHasError = missingHoldingsIds.some(hId => id.includes(hId)) || (showMissingCostBasisBanner && isHoldingMissingCostBasis(original, assetType));

    // Check for lot-specific errors when tax details are shown
    if (showTaxDetails) {
      if (isLotRow) {
        // This is a lot subrow - check its validation
        const lot = original as unknown as Lot;
        rowHasError = rowHasError || hasLotValidationError(lot);
      } else if (original.lots?.length) {
        // This is a parent holding - check if any of its lots have errors
        rowHasError = rowHasError || original.lots.some(hasLotValidationError);
      }
    }

    const rowIsDisabled = hasMissingHoldings && !missingHoldingsIds.some(hId => id.includes(hId));
    const rowIsFirstError = firstMissingHolding?._id === id;

    return classNames({
      disabled: rowIsDisabled,
      'has-error': rowHasError,
      'is-first-error': rowIsFirstError,
    });
  };

  const computeTableClasses = () => {
    const defaultClass = 'portfolio-editor-holdings-table';
    const hasKYPColumn = isCompareAlternativeColumnVisible;

    if (!hasMissingHoldings) {
      return classNames(defaultClass, {
        'with-tax-fields': showTaxDetails,
        'with-kyp-column': hasKYPColumn,
      });
    }

    // This returns a class based on the current replace product step
    return classNames(defaultClass, {
      'missing-product-initial-step': missingProductStep === MISSING_PRODUCT_STEP.INITIAL,
      'missing-product-search-step': missingProductStep === MISSING_PRODUCT_STEP.SEARCH,
      'missing-product-open-modal-step': missingProductStep === MISSING_PRODUCT_STEP.OPEN_MODAL,
      'with-tax-fields': showTaxDetails,
      'with-kyp-column': hasKYPColumn,
    });
  };

  // HEADER

  const getHeader = (i18nKey: string): string => formatMessage(`${i18nBasePath}.Columns.${i18nKey}`);

  // ASYNC CELL FUNCTIONS

  const AsyncCodeCell: React.FC<{ portfolioHolding: PortfolioHolding }> = React.memo(({ portfolioHolding }) => {
    const [symbols, setSymbols] = useState<string[] | null>(null);
    const holdingId = portfolioHolding.original.id;
    const holdingType = portfolioHolding.original.type;
    const { yieldParams } = portfolioHolding.original;

    useEffect(() => {
      const fetchData = async () => {
        if (!helpers.holdingIsMissingOrInvalid(portfolioHolding)) {
          try {
            const data: Fund = (await getHoldingData(portfolioHolding)) as Fund;
            const { symbols } = data;

            if (symbols) {
              setSymbols(symbols);
            }
          } catch (error) {
            console.warn('Failed to fetch holding data for symbols:', holdingId);
          }
        }
      };

      fetchData();
    }, [holdingId, holdingType, yieldParams]);

    if (symbols && symbols.length > 1) {
      return (
        <SelectInput
          className="select-input-v2"
          data-testid="dropdown-code"
          onValueChange={(value: string) => onSymbolChange(portfolioHolding, value)}
          selected={portfolioHolding.symbol}
        >
          {symbols.map(symbol => (
            <SelectOption key={symbol} value={symbol}>
              {symbol}
            </SelectOption>
          ))}
        </SelectInput>
      );
    }

    return <span>{portfolioHolding.symbol}</span>;
  });

  const AsyncUnitCell: React.FC<{ portfolioHolding: PortfolioHolding }> = React.memo(({ portfolioHolding }) => {
    const { _id, units } = portfolioHolding;
    const [holdingData, setHoldingData] = useState<Holding>();
    const holdingId = portfolioHolding.original.id;
    const holdingType = portfolioHolding.original.type;
    const { yieldParams } = portfolioHolding.original;

    useEffect(() => {
      const fetchData = async () => {
        try {
          const data: Fund = (await getHoldingData(portfolioHolding)) as Fund;
          setHoldingData(data);
        } catch (error) {
          console.warn('Failed to fetch holding data for units:', holdingId);
        }
      };

      fetchData();
    }, [holdingId, holdingType, yieldParams]);

    const latestHoldingPrice = holdingData ? getLatestPriceForHolding(portfolioHolding.original.type, holdingData) : undefined;
    const isInputDisabled = !latestHoldingPrice;
    const placeholder = isInputDisabled ? '' : '0';
    const hasUnitsError = !isInputDisabled && (!units || units === 0);

    if (holdingData) {
      return (
        <Tooltip className="custom-tooltip-v2" message={formatMessage(`${i18nBasePath}.Inputs.UnitsDisabled`)} disabled={!isInputDisabled}>
          <UnitInput
            className={classNames('unit-input-v2', { 'has-error': hasUnitsError })}
            controlled={false}
            disabled={isInputDisabled}
            key={`unit-${_id}`}
            min={0}
            onValueChange={(value: string) => onUnitsChange(portfolioHolding, Number(value), holdingData)}
            placeholder={placeholder}
            type="number"
            value={units}
            data-testid="input-unit"
          />
        </Tooltip>
      );
    }
    return <UnitInput className="unit-input-v2" disabled placeholder="" />;
  });

  // CELL FUNCTIONS
  const getAdvisoryFeeCell = (portfolioHolding: PortfolioHolding): JSX.Element => {
    const { _id } = portfolioHolding;

    return (
      <UnitInput
        className="unit-input-v2"
        controlled={false}
        disabled
        key={`advisory-fee-${_id}`}
        min={0}
        onValueChange={(value: string) => onAdvisoryFeeChange(portfolioHolding, Number(value))}
        placeholder="0"
        symbolSection={UnitSymbolSection.RIGHT}
        type="number"
        unitSymbol="BPS"
        value=""
      />
    );
  };

  const getCompareAlternatives = (portfolioHolding: PortfolioHolding): JSX.Element | null => {
    const hasComplianceAlternatives = complianceAlternatives.length > 0 && complianceAlternatives[0]._id === portfolioHolding.original.id;
    const originalHolding = portfolioHolding.original;

    const shouldShowKYPForFunds = originalHolding.type === HOLDING_TYPES.FUND && userHasPermission(user, PERMISSIONS.KYP);
    const shouldShowKYPForStocks = originalHolding.type === HOLDING_TYPES.STOCK && userHasPermission(user, PERMISSIONS.KYP_STOCKS);
    const showKYPComplianceIcon =
      originalHolding.id &&
      (shouldShowKYPForFunds || shouldShowKYPForStocks) &&
      (portfolioHolding.flags?.includes(ShelfStatusEnum.ON_SHELF) || portfolioHolding.flags?.includes(ShelfStatusEnum.ON_SHELF_KYP_COMPLETE));

    const requiredPermissions = originalHolding.type === HOLDING_TYPES.STOCK ? [PERMISSIONS.KYP_STOCKS] : [PERMISSIONS.KYP];

    const shouldDisplay = showKYPComplianceIcon && !hasComplianceAlternatives && !isHoldingACustomProxy(customProxies, portfolioHolding);
    return shouldDisplay ? (
      <ConnectedPrivateComponent permissions={requiredPermissions}>
        {() => (
          <PrimaryLink className="compare-alternatives-btn" onClick={() => compareActiveHoldingAlternatives(portfolioHolding)}>
            <i className="icon-ds-compare-arrows" aria-hidden="true" />
            <FormattedMessageV2 toLocalize={`${i18nBasePath}.CompareAlternatives.Action`} />
          </PrimaryLink>
        )}
      </ConnectedPrivateComponent>
    ) : null;
  };

  const getDeleteHoldingCell = (portfolioHolding: PortfolioHolding): JSX.Element => {
    const isFirstError = firstMissingHolding?._id === portfolioHolding._id;
    const rowHasError = missingHoldingsIds.some(hId => portfolioHolding._id.includes(hId));
    const isDisabled = !isFirstError && rowHasError;

    const deleteButton = (
      <IconButton className="tst-hover-action-btn" icon="remove-new" onClick={() => removeHoldingFromPortfolio(portfolioHolding)} disabled={isDisabled} />
    );

    return !isDisabled ? (
      <Tooltip className="custom-tooltip-v2" message={formatMessage(`${i18nBasePath}.Columns.Action.Delete`)} place="bottom">
        {deleteButton}
      </Tooltip>
    ) : (
      deleteButton
    );
  };

  const getDollarsCell = useCallback(
    (portfolioHolding: PortfolioHolding): JSX.Element => {
      const { _id, dollars } = portfolioHolding;
      const isMissingCashValue = dollars === 0;

      return (
        <UnitInput
          className={classNames('unit-input-v2', { 'has-error': isMissingCashValue })}
          controlled={false}
          key={`dollars-${_id}`}
          min={0}
          onValueChange={(value: string) => onDollarsChange(portfolioHolding, Number(value))}
          placeholder="0"
          symbolSection={UnitSymbolSection.LEFT}
          type="number"
          unitSymbol="$"
          value={dollars || ''}
        />
      );
    },
    [onDollarsChange],
  );

  // FIXME : there are so many css rules for the name that this is just not enough
  const getNameMaxLimit = () => {
    const tableWidth = holdingsTableRef.current?.offsetWidth || 0;

    // Next values come from the referenced table and are used to truncate the name
    if (tableWidth >= 1342) return 70;
    if (tableWidth >= 1268) return 62;
    if (tableWidth >= 1182) return 50;
    return 17;
  };

  const getNameCell = (row: Row<PortfolioHolding>, isLot?: boolean): Nullable<JSX.Element> => {
    const portfolioHolding = isLot ? row.getParentRow()?.original : row.original;

    if (!portfolioHolding) return null;

    const name = formatLocalizedString(portfolioHolding.name);

    if (!name) return null;

    const holdingIsMissing = helpers.holdingIsMissingOrInvalid(portfolioHolding);
    const holdingNameCell = <span className="holding-name-cell">{name}</span>;

    if (holdingIsMissing) {
      return (
        <Tooltip
          className="custom-tooltip-v2 missing-product"
          title={formatMessage(`${i18nBasePath}.MissingHoldings.MissingProduct`)}
          message={formatMessage(`${i18nBasePath}.MissingHoldings.MissingProductCannotBeFound`, { product: helpers.getTrimmedHoldingName(name) })}
          place="bottom"
        >
          <div>
            <i className="icon-exclamation-outline" />
            {holdingNameCell}
          </div>
        </Tooltip>
      );
    }

    const { element: shelfStatusElement, hasShelfStatus } = buildShelfStatus(portfolioHolding);

    if (name.length <= getNameMaxLimit() && !showTaxDetails) {
      return (
        <>
          {holdingNameCell}
          {shelfStatusElement}
        </>
      );
    }

    return (
      <div
        className={classNames('holding-name', {
          'with-tooltip': true,
          'with-shelf-status': hasShelfStatus,
          'with-compare-alternatives': getCompareAlternatives(portfolioHolding) !== null,
        })}
      >
        <Tooltip className="custom-tooltip-v2" message={name} place="top">
          {holdingNameCell}
        </Tooltip>
        {shelfStatusElement}
      </div>
    );
  };

  /**
   * Builds the shelf status component for a portfolio holding.
   * @param portfolioHolding - The portfolio holding to render shelf status for
   * @returns An object containing the ShelfStatus element (or null if no flags) and a boolean indicating presence of shelf status
   */
  const buildShelfStatus = (portfolioHolding: PortfolioHolding): { element: JSX.Element | null; hasShelfStatus: boolean } => {
    const { flags } = portfolioHolding;

    // Use the universal helper that mirrors convertShelvesToShelfStatuses logic
    const shelfName = user ? getShelfNameFromShelvableInstrument(portfolioHolding, user) : undefined;

    const hasShelfStatus = !!flags?.length;

    return {
      element: hasShelfStatus ? <ShelfStatus isV2 statuses={flags} title={shelfName} tooltipClassName="custom-tooltip-v2" /> : null,
      hasShelfStatus,
    };
  };

  const getWeightCell = (portfolioHolding: PortfolioHolding): JSX.Element => {
    const { _id, weight } = portfolioHolding;

    const isSizeInvalid = userHasPermission(user, PERMISSIONS.RESTRICTED_MISSING_ITEM_FLOW) ? false : !portfolio.size;

    return (
      <Tooltip className="custom-tooltip-v2" message={formatMessage(`${i18nBasePath}.Inputs.WeightDisabled`)} disabled={!isSizeInvalid}>
        <UnitInput
          className="unit-input-v2"
          controlled={false}
          disabled={isSizeInvalid}
          key={`weight-${_id}`}
          min={0}
          onValueChange={(value: string) => onWeightChange(portfolioHolding, Number(value))}
          placeholder="0"
          symbolSection={UnitSymbolSection.RIGHT}
          type="number"
          unitSymbol="%"
          value={weight || ''}
          data-testid="input-weight"
        />
      </Tooltip>
    );
  };

  const getCostBasisCell = (row: Row<PortfolioHolding>, isLot?: boolean): JSX.Element => {
    const holdingRow = !isLot ? row.original : row.getParentRow()?.original;
    const assetType = !isLot && holdingRow?.original?.id && assetTypeCache ? assetTypeCache.get(holdingRow.original.id) : undefined;
    const isInputDisabled = !isLot ? isInputDisabledForCostBasis(row.original, assetType) : false;

    // For non-lot rows, return empty (purchase date only applies to lots)
    if (!isLot && row.original.lots?.length) {
      return <span />;
    }

    if (isInputDisabled) {
      return (
        <Tooltip className="custom-tooltip-v2" message={formatMessage(`${i18nBasePath}.Inputs.CostBasisDisabled`)} disabled={!isInputDisabled}>
          <UnitInput className="unit-input-v2" disabled={isInputDisabled} />
        </Tooltip>
      );
    }

    const lotRow = !isLot ? undefined : (row.original as unknown as Lot);

    if (!holdingRow) {
      return <span />;
    }

    const costBasis = !isLot ? holdingRow.taxSettings?.costBasis || '' : lotRow?.costBasis || '';

    return (
      <Tooltip className="custom-tooltip-v2" message={formatMessage(`${i18nBasePath}.Inputs.UnitsDisabled`)} disabled={!isInputDisabled}>
        <UnitInput
          className={classNames('unit-input-v2', { 'has-error': !costBasis })}
          controlled={false}
          disabled={false}
          key={`unit-${!isLot ? holdingRow._id : lotRow?._id}`}
          min={0}
          onValueChange={value => onCostBasisChange(Number(value), holdingRow._id, lotRow?._id)}
          placeholder="0"
          type="number"
          value={costBasis}
          data-testid="input-unit"
        />
      </Tooltip>
    );
  };

  const getPurchaseDateCell = (row: Row<PortfolioHolding>, isLot?: boolean): JSX.Element => {
    const holdingRow = !isLot ? row.original : row.getParentRow()?.original;
    const assetType = !isLot && holdingRow?.original?.id && assetTypeCache ? assetTypeCache.get(holdingRow.original.id) : undefined;
    const isInputDisabled = !isLot ? isInputDisabledForCostBasis(row.original, assetType) : false;

    // For non-lot rows, return empty (purchase date only applies to lots)
    if (!isLot && row.original.lots?.length) {
      return <span />;
    }

    if (isInputDisabled) {
      return (
        <Tooltip className="custom-tooltip-v2" message={formatMessage(`${i18nBasePath}.Inputs.PurchaseDateDisabled`)} disabled={!isInputDisabled}>
          <UnitInput className="unit-input-v2" disabled={isInputDisabled} />
        </Tooltip>
      );
    }

    const lotRow = !isLot ? undefined : (row.original as unknown as Lot);

    if (!holdingRow) {
      return <span />;
    }

    let purchaseDate: Date | undefined;
    if (!isLot) {
      purchaseDate = holdingRow.taxSettings?.purchaseDate;
    } else if (lotRow?.purchaseDate) {
      purchaseDate = lotRow.purchaseDate;
    }

    return (
      <DatePicker
        compact
        allowPrevNext2
        className="holding-table-date-picker"
        maxDate={new Date()}
        onDateChange={date => onPurchaseDateChange(date, holdingRow._id!, lotRow?._id)}
        defaultDate={purchaseDate}
        customFormat={formatMessageWithLocale('Common.DateFormats.Short', locale)}
      />
    );
  };

  // EMPTY TABLE DATA

  const getNoResultsComponent = (): JSX.Element => {
    const i18nEmptyTablePath = `${i18nBasePath}.EmptyData.NoResults`;
    return (
      <EmptySearchResults
        searchQuery={portfolioSearchQuery}
        titleToLocalize={`${i18nEmptyTablePath}.Title`}
        messageToLocalize={`${i18nEmptyTablePath}.Message`}
      />
    );
  };

  const getNoHoldingsComponent = (): JSX.Element => {
    const { user } = props;

    const i18nEmptyTablePath = `${i18nBasePath}.EmptyData.NoHoldings`;
    const canUploadCSV = canAccessUploadCSV(user);
    const canUseAIStatementReader = canAccessAIStatementReader(user);

    if (!canUploadCSV && !canUseAIStatementReader) {
      return <EmptyStateCard titlePath={`${i18nEmptyTablePath}.Title`} messagePath={`${i18nEmptyTablePath}.Message`} />;
    }
    if (canUseAIStatementReader) {
      const i18nEmptyTablePathAIStatementReader = `${i18nEmptyTablePath}.AIStatementReader`;
      const secondaryActions = {
        links: [
          {
            icon: 'upload-new',
            label: `${i18nEmptyTablePathAIStatementReader}.Button`,
            onClick: () => {
              resetProcessing();
              setPortfolioAIStatementReaderModal(true);
            },
          },
        ],
      };
      return (
        <EmptyStateCard
          imagePath={EmptyPorfolioAIStatementReader}
          titlePath={`${i18nEmptyTablePathAIStatementReader}.Title`}
          messagePath={`${i18nEmptyTablePathAIStatementReader}.Message`}
          {...secondaryActions}
        />
      );
    }
    const secondaryActions = {
      links: [{ icon: 'upload-new', label: `${i18nEmptyTablePath}.Button`, onClick: () => setShowPortfolioUploadModal(true) }],
    };

    return <EmptyStateCard titlePath={`${i18nEmptyTablePath}.Title`} messagePath={`${i18nEmptyTablePath}.MessageUploadCSV`} {...secondaryActions} />;
  };

  const getEmptyDataComponent = (): JSX.Element => (portfolioSearchQuery ? getNoResultsComponent() : getNoHoldingsComponent());

  const renderReplaceAction = useCallback(
    (portfolioHolding: PortfolioHolding) => (
      <PortfolioEditorHoldingsTableReplaceAction
        portfolioHolding={portfolioHolding}
        hasRestrictedFlow={hasRestrictedFlow}
        customProxies={customProxies}
        isFocusedQuickSearch={isFocusedQuickSearch}
        i18nBasePath={i18nBasePath}
        setMissingProductStep={setMissingProductStep}
        setHoldingToReplace={setHoldingToReplace}
        setFocusedQuickSearchEditPorfolio={setFocusedQuickSearchEditPorfolio}
        setReplacementProxyItem={setReplacementProxyItem}
        disabled={!(firstMissingHolding?._id === portfolioHolding._id)}
      />
    ),
    [
      hasRestrictedFlow,
      customProxies,
      firstMissingHolding,
      isFocusedQuickSearch,
      setMissingProductStep,
      setHoldingToReplace,
      setFocusedQuickSearchEditPorfolio,
      setReplacementProxyItem,
    ],
  );

  /**
   * Determines if a table row represents a tax lot (subrow) rather than a parent holding.
   * Lots are displayed as expandable subrows when the tax analysis lots feature is enabled.
   *
   * @param row - The table row to check
   * @returns true if the row is a lot subrow, false if it's a parent holding
   */
  const isLotRow = useCallback((row: Row<PortfolioHolding>): boolean => canAccessLots && row.depth > 0, [canAccessLots]);

  /**
   * Determines if a value should display as read-only.
   * This occurs when the lots feature is enabled, tax details are shown, and the holding has lots.
   *
   * @param hasLots - If holding has lots
   * @returns true if the value should display read-only
   */
  const shouldDisplayValueAsReadOnly = useCallback(
    (hasLots: boolean): boolean => canAccessLots && !!showTaxDetails && hasLots,
    [canAccessLots, showTaxDetails],
  );

  // TABLE COLUMNS
  const getReadOnlyColumns = (): ColumnDef<PortfolioHolding>[] => [
    {
      accessorKey: PORTFOLIO_HOLDINGS_KEYS.SYMBOL,
      cell: ({ row }) => {
        const isLot = row.depth > 0;
        return isLot ? null : row.original.symbol;
      },
      header: getHeader('Code'),
      size: isMobile || isTablet ? 80 : 160,
    },
    {
      cell: '',
      header: '',
      id: 'tooltipColumn',
      size: 24,
    },
    {
      accessorKey: PORTFOLIO_HOLDINGS_KEYS.NAME,
      cell: ({ row }) => getNameCell(row, isLotRow(row)),
      header: getHeader('Name'),
      sortingFn: customSorting,
      ...(isMobile ? { size: 120 } : {}),
      ...(isTablet ? { size: 180 } : {}),
    },
    {
      accessorKey: PORTFOLIO_HOLDINGS_KEYS.UNITS,
      header: getHeader('Units'),
      size: 100,
    },
    {
      accessorKey: PORTFOLIO_HOLDINGS_KEYS.WEIGHT,
      cell: ({ row }) => `${row.original.weight?.toFixed(2)}%`,
      header: getHeader('Weight'),
      enableHiding: true,
      size: 100,
    },
    {
      accessorKey: PORTFOLIO_HOLDINGS_KEYS.DOLLARS,
      cell: ({ row }) => `$ ${row.original.dollars?.toFixed(2)}`,
      header: getHeader('Dollars'),
      size: isMobile ? 80 : 160,
    },
  ];

  const getRegularColumns = (): ColumnDef<PortfolioHolding>[] => {
    const hideWhenMissing: PORTFOLIO_HOLDINGS_KEYS[] = [PORTFOLIO_HOLDINGS_KEYS.UNITS, PORTFOLIO_HOLDINGS_KEYS.WEIGHT, PORTFOLIO_HOLDINGS_KEYS.ALTERNATIVES];
    const shouldDisplayTaxFields = isTaxAnalysisFeatureEnabled && showTaxDetails;
    const desktopWidthForSymbolColumn = shouldDisplayTaxFields ? 120 : 160;

    const columns: ColumnDef<PortfolioHolding>[] = [
      {
        accessorKey: PORTFOLIO_HOLDINGS_KEYS.SYMBOL,
        cell: ({ row }) => (isLotRow(row) ? null : <AsyncCodeCell portfolioHolding={row.original} />),
        header: getHeader('Code'),
        meta: { customClass: 'select-input-alignment' },
        size: isMobile || isTablet ? 80 : desktopWidthForSymbolColumn,
      },
      {
        id: 'tooltipColumn',
        header: '',
        cell: '',
        enableSorting: false,
        size: 24,
      },
      {
        accessorKey: PORTFOLIO_HOLDINGS_KEYS.NAME,
        cell: ({ row }) => getNameCell(row, isLotRow(row)),
        header: getHeader('Name'),
        sortingFn: customSorting,
        ...(isMobile ? { size: 120 } : {}),
        ...(isTablet ? { size: 180 } : {}),
      },
      ...(isCompareAlternativeColumnVisible
        ? [
            {
              accessorKey: PORTFOLIO_HOLDINGS_KEYS.ALTERNATIVES,
              cell: ({ row }: { row: Row<PortfolioHolding> }) => (!isLotRow(row) ? getCompareAlternatives(row.original) : null),
              enableHiding: true,
              enableSorting: false,
              header: '',
              id: PORTFOLIO_HOLDINGS_OTHER_TABLE_COLUMNS.ACTION_COMPARE_ALTERNATIVES,
              meta: { customClass: 'compare-alternatives-cell' },
              size: 190,
            },
          ]
        : []),
      ...(shouldDisplayTaxFields
        ? [
            {
              accessorKey: 'purchaseDate',
              cell: ({ row }: { row: Row<PortfolioHolding> }) => getPurchaseDateCell(row, isLotRow(row)),
              header: getHeader('PurchaseDate'),
              enableHiding: true,
              size: 140,
            },
            {
              accessorKey: 'costBasis',
              cell: ({ row }: { row: Row<PortfolioHolding> }) => getCostBasisCell(row, isLotRow(row)),
              header: getHeader('CostBasis'),
              enableHiding: true,
              size: isMobile ? 140 : 160,
            },
          ]
        : []),
      {
        accessorKey: PORTFOLIO_HOLDINGS_KEYS.UNITS,
        cell: ({ row }) => {
          // Handle lot rows (subrows with editable units)
          if (isLotRow(row)) {
            const lotRow = row.original as unknown as Lot;
            const holdingRow = row.getParentRow()?.original;

            if (!lotRow._id || !holdingRow) {
              return <></>;
            }

            const hasUnitsError = !lotRow.units || lotRow.units === 0;

            return (
              <UnitInput
                className={classNames('unit-input-v2', { 'has-error': hasUnitsError })}
                controlled={false}
                disabled={false}
                key={`lot-unit-${lotRow._id}`}
                min={0}
                onValueChange={(value: string) => onLotUnitsChange(Number(value), lotRow._id!, holdingRow._id)}
                placeholder="0"
                type="number"
                value={lotRow.units || ''}
                data-testid="input-lot-unit"
              />
            );
          }

          // When lots feature is enabled, show holding units as read-only text at holding level
          if (shouldDisplayValueAsReadOnly(!!row.original.lots?.length) && row.original.units != null) {
            return (
              <span data-private data-dd-privacy="mask">
                {formatNumber(row.original.units, 2, locale)}
              </span>
            );
          }

          if (helpers.holdingIsMissingOrInvalid(row.original)) return null;
          return <AsyncUnitCell portfolioHolding={row.original} />;
        },
        header: getHeader('Units'),
        enableHiding: true,
        size: 100,
      },
      {
        accessorKey: PORTFOLIO_HOLDINGS_KEYS.WEIGHT,
        cell: ({ row }) => {
          if (isLotRow(row)) {
            return null;
          }

          // When lots feature is enabled, show holding weight as read-only text at holding level
          if (shouldDisplayValueAsReadOnly(!!row.original.lots?.length) && row.original.weight != null) {
            return (
              <span data-private data-dd-privacy="mask">
                {formatNumber(row.original.weight, 2, locale)}
              </span>
            );
          }

          if (helpers.holdingIsMissingOrInvalid(row.original)) return null;
          return getWeightCell(row.original);
        },
        header: getHeader('Weight'),
        enableHiding: true,
        size: 100,
      },
      {
        accessorKey: PORTFOLIO_HOLDINGS_KEYS.DOLLARS,
        cell: ({ row }) => {
          // Handle lot rows (subrows with editable dollars)
          if (isLotRow(row)) {
            const lotRow = row.original as unknown as Lot;
            const holdingRow = row.getParentRow()?.original;

            if (!lotRow._id || !holdingRow) {
              return <></>;
            }

            return (
              <UnitInput
                className="unit-input-v2"
                controlled={false}
                key={`lot-dollars-${lotRow._id}`}
                min={0}
                onValueChange={(value: string) => onLotDollarsChange(Number(value), lotRow._id!, holdingRow._id)}
                placeholder="0"
                symbolSection={UnitSymbolSection.LEFT}
                type="number"
                unitSymbol="$"
                value={lotRow.dollars || ''}
                data-testid="input-lot-dollar"
              />
            );
          }

          // When lots feature is enabled, show holding dollars as read-only text at holding level
          if (shouldDisplayValueAsReadOnly(!!row.original.lots?.length) && row.original.dollars != null) {
            return (
              <span data-private data-dd-privacy="mask">
                {formatCurrency(row.original.dollars)}
              </span>
            );
          }

          if (helpers.holdingIsMissingOrInvalid(row.original)) {
            return renderReplaceAction(row.original);
          }

          // Default: editable dollars input
          return getDollarsCell(row.original);
        },
        header: getHeader('Dollars'),
        size: isMobile ? 140 : 160,
      },
      {
        accessorKey: PORTFOLIO_HOLDINGS_KEYS.ADVISORY_FEE,
        cell: ({ row }) => {
          if (isLotRow(row)) return null;

          if (helpers.holdingIsMissingOrInvalid(row.original)) return null;
          return getAdvisoryFeeCell(row.original);
        },
        header: getHeader('AdvisoryFee'),
        enableHiding: true,
        size: 124,
      },
      {
        cell: ({ row }) => (isLotRow(row) ? null : getDeleteHoldingCell(row.original)),
        enableSorting: false,
        header: '',
        id: PORTFOLIO_HOLDINGS_OTHER_TABLE_COLUMNS.ACTION_DELETE,
        size: 48,
      },
    ];

    const shouldHide = missingHoldingsCount > 0;
    // not all columns has accessorKey, so we need to check that before filtering
    return filter(columns, col =>
      shouldHide && 'accessorKey' in col && col.accessorKey ? !includes(hideWhenMissing, col.accessorKey as PORTFOLIO_HOLDINGS_KEYS) : true,
    );
  };

  const getTableColumns: ColumnDef<PortfolioHolding>[] = useMemo(
    () => (isReadOnly ? getReadOnlyColumns() : getRegularColumns()),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isReadOnly, locale, renderReplaceAction, getDollarsCell, isMobile, isTablet, missingHoldingsCount, showTaxDetails],
  );

  const renderBanner = (variant: BannerVariant, text: string) => <Banner showCloseButton={false} variant={variant} size="slim" text={text} />;

  const handleProxyModalClose = useCallback(() => {
    setMissingProductStep(MISSING_PRODUCT_STEP.INITIAL);
    setReplacementProxyItem(undefined);
  }, []);

  const renderMissingCostBasisBanner = () => {
    if (showMissingCostBasisBanner && Array.isArray(missingCostBasisPages) && missingCostBasisPages.length > 0) {
      return renderBanner(
        BannerVariant.Error,
        formatMessage('PortfolioEditorBuilder.Content.MissingCostBasisPages', {
          pageList: missingCostBasisPages.map(page => page + 1).join(', '),
        }),
      );
    }
    return null;
  };

  return (
    <div ref={holdingsTableRef} className={computeTableClasses()} data-testid="portfolio-editor-holdings-table">
      {renderMissingCostBasisBanner()}

      {hasMissingHoldings && (
        <>
          {renderBanner(BannerVariant.Error, formatMessage(`${i18nBasePath}.MissingHoldings.PleaseReplace`))}
          <div className="missing-holdings-overlay" />
        </>
      )}
      {replacedMissingHoldings && <>{renderBanner(BannerVariant.Success, formatMessage(`${i18nBasePath}.MissingHoldings.ConflictsResolved`))}</>}

      <DataTableV2
        enablePagination
        isExpandable={showTaxDetails}
        tableRef={tableRef}
        columns={getTableColumns}
        columnVisibilityState={columnVisibility}
        data={portfolioHoldingsData}
        enableSorting={!hasMissingHoldings}
        getAdditionalRowClassNames={computeAdditionalRowClassNames}
        noDataComponent={getEmptyDataComponent}
        tableClassNames={tableClassNames}
        tableTotalText={formatMessage(`${i18nBasePath}.PaginationTotal`, { total: portfolioHoldingsData.length })}
        tableBottomAction={
          portfolioHoldingsData.length > 0 && !isReadOnly
            ? {
                label: formatMessage(`${i18nBasePath}.ClearAll.Label`),
                onClick: props.displayClearHoldingsModal,
                ariaLabel: formatMessage(`${i18nBasePath}.ClearAll.AriaLabel`),
                dataCy: 'portfolioEditorHoldingsTable-link-clearAll',
              }
            : undefined
        }
        getSubRows={
          canAccessLots
            ? row => row.lots as PortfolioHolding[] | undefined // TODO: CONN-11492 Tanstack expects the same type for rows and subRows
            : undefined
        }
      />

      <PortfolioUploadModal
        show={showPortfolioUploadModal}
        onCancel={() => setShowPortfolioUploadModal(false)}
        titleId="EmptyComparison.UploadPortfolio.Title"
        backdropClassName="modal-v2-backdrop"
      />

      {canAccessMultiPortfoliosStatementReader(user) ? (
        <MultiPortfolioAiStatementReaderModal
          show={showPortfolioAIStatementReaderModal}
          onCancel={() => setPortfolioAIStatementReaderModal(false)}
          locale={locale}
        />
      ) : (
        <PortfolioAiStatementReaderModal
          show={showPortfolioAIStatementReaderModal}
          onCancel={() => setPortfolioAIStatementReaderModal(false)}
          locale={locale}
        />
      )}

      <StatusFeedbackModal show={isAddingHoldingsFromCSV} status={Status.LOADING} i18nBasePath="PortfolioEditorBuilder" isDelayed withBackdrop />

      {holdingToCompareComplianceAlts && (
        <CompareAlternatives
          portfolioHolding={holdingToCompareComplianceAlts}
          show={showCompareAlternativesModal}
          closeModal={onCompareAlternativesForComplianceClose}
        />
      )}

      {missingProductStep === MISSING_PRODUCT_STEP.OPEN_MODAL && replacementProxyItem && (
        <RateOfReturnModal
          show
          proxyName={replacementProxyItem.name}
          options={replacementProxyItem.options!}
          filteredShelves={replacementProxyItem.filteredShelves}
          holdingToReplace={holdingToReplace}
          onClosed={handleProxyModalClose}
        />
      )}
    </div>
  );
};

const mapStateToProps = (state: ReduxState): StateProps => {
  const missingHoldings = getPortfolioMissingOrInvalidHoldings(selectShelfStatusedPortfolioHoldings(state.editPortfolio.portfolio));
  const missingHoldingsIds = missingHoldings?.map(({ _id }) => _id) ?? [];

  return {
    isAddingHoldingsFromCSV: !!state.ui.loaders.actions.find(loader => [ADD_HOLDINGS_TO_PORTFOLIO_FROM_CSV.type].includes(loader.name)),
    complianceAlternatives: state.complianceAlternatives,
    customProxies: state.customProxies.customProxies,
    firstMissingHolding: missingHoldings.find(h => h !== undefined),
    hasRestrictedFlow: userHasPermission(state.auth.user, PERMISSIONS.RESTRICTED_MISSING_ITEM_FLOW),
    holdingToReplace: state.editPortfolio.holdingToReplace,
    isFocusedQuickSearch: state.quickSearchEditPortfolio.focused,
    isReadOnly: state.comparison.portfolioEditor.isReadOnly,
    holdingToCompareComplianceAlts: state.editPortfolio.holdingToCompareComplianceAlts,
    locale: state.intl.locale,
    missingHoldingsIds,
    portfolio: state.editPortfolio.portfolio,
    productAdded: state.editPortfolio.productAdded,
    user: state.auth.user,
    comparisonItem: state.editPortfolio.comparisonItem,
    isPortfolioValueLocked: state.editPortfolio.isPortfolioValueLocked,
    showTaxDetails: state.editPortfolio.showTaxDetails,
  };
};

export default connect(mapStateToProps, {
  changeHoldingDollars,
  changeHoldingCostBasis,
  changeHoldingLotDollars,
  changeHoldingPurchaseDate,
  changeHoldingLotUnits,
  changeHoldingSymbol,
  changeHoldingUnits,
  changeHoldingWeight,
  clearProductAdded,
  getCustomProxies,
  recalculatePortfolioAllocation,
  removeHoldingFromPortfolio,
  resetProcessing,
  setFocusedQuickSearchEditPorfolio,
  setHoldingToCompareComplianceAlts,
  setHoldingToReplace,
  calculatePortfolio,
})(withErrorBoundary(PortfolioEditorHoldingsTable));
